import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../common/Modal';
import ConfirmDialog from '../ui/ConfirmDialog';
import WizardSelect from '../ui/WizardSelect';
import { setFlashToast } from '../../utils/flashToast';
import { listDocumentMovementOfficers, documentMoverLabel } from '../../services/taskForwarding';
import {
  assignClosureApprover,
  closureApproverLabel,
  getAuthorizedClosureApprovers,
  getNonAuthorizedClosureApprovers,
  nonAuthorizedClosureApproverLabel,
  type ExistingClosureAssignment,
} from '../../services/taskClosure';

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
      {message}
    </div>
  );
}

type LoadState = 'idle' | 'loading' | 'error';

type SelectUserForTaskClosureModalProps = {
  open: boolean;
  /** workflow_instance_stage_user_task_id */
  workflowInstanceStageUserTaskId: string;
  blockingInfoMessage?: string;
  blockedBySubsidiaryTasks?: boolean;
  initialAssignment?: ExistingClosureAssignment;
  onClose: () => void;
  onSuccess: () => void;
};

function ensureOption(
  options: { id: string; label: string }[],
  id?: string,
  label?: string,
): { id: string; label: string }[] {
  if (!id?.trim()) return options;
  if (options.some((o) => o.id === id)) return options;
  return [{ id, label: label?.trim() || id }, ...options];
}

/** Data dictionary: Intranet — Select User for Task Closure pop-up. */
export function SelectUserForTaskClosureModal({
  open,
  workflowInstanceStageUserTaskId,
  blockingInfoMessage,
  blockedBySubsidiaryTasks = false,
  initialAssignment,
  onClose,
  onSuccess,
}: SelectUserForTaskClosureModalProps) {
  const { t } = useTranslation();
  const [authorizedId, setAuthorizedId] = useState('');
  const [nonAuthorizedId, setNonAuthorizedId] = useState('');
  const [physicalRequired, setPhysicalRequired] = useState(true);
  const [documentMoverId, setDocumentMoverId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const [authorizedState, setAuthorizedState] = useState<LoadState>('idle');
  const [authorizedOptions, setAuthorizedOptions] = useState<{ id: string; label: string }[]>([]);

  const [nonAuthorizedState, setNonAuthorizedState] = useState<LoadState>('idle');
  const [nonAuthorizedOptions, setNonAuthorizedOptions] = useState<{ id: string; label: string }[]>([]);

  const [moversState, setMoversState] = useState<LoadState>('idle');
  const [moverOptions, setMoverOptions] = useState<{ id: string; label: string }[]>([]);

  const [submitting, setSubmitting] = useState(false);

  const isUpdateMode = Boolean(initialAssignment);

  useEffect(() => {
    if (!open) return;

    /* eslint-disable react-hooks/set-state-in-effect */
    setAuthorizedId(initialAssignment?.authorizedClosureApproverId ?? '');
    setNonAuthorizedId(initialAssignment?.nonAuthorizedClosureApproverId ?? '');
    setPhysicalRequired(initialAssignment?.physicalFileMovementRequired ?? true);
    setDocumentMoverId(initialAssignment?.documentMoverId ?? '');
    setErrors({});
    setCancelConfirmOpen(false);
    /* eslint-enable react-hooks/set-state-in-effect */

    setAuthorizedState('loading');
    setAuthorizedOptions([]);
    setNonAuthorizedState('loading');
    setNonAuthorizedOptions([]);
    setMoversState('loading');
    setMoverOptions([]);

    const authController = new AbortController();
    const nonAuthController = new AbortController();
    const moverController = new AbortController();

    void getAuthorizedClosureApprovers({ taskId: workflowInstanceStageUserTaskId, signal: authController.signal })
      .then((r) => {
        if (authController.signal.aborted) return;
        if (!r.success) {
          setAuthorizedState('error');
          setFlashToast({ message: r.message || 'Failed to load pre-authorized closure approvers.', variant: 'error' });
          return;
        }
        const opts = r.data.map(closureApproverLabel).filter((x): x is { id: string; label: string } => Boolean(x));
        setAuthorizedOptions(
          ensureOption(
            opts,
            initialAssignment?.authorizedClosureApproverId,
            initialAssignment?.authorizedClosureApproverLabel,
          ),
        );
        setAuthorizedState('idle');
      })
      .catch(() => {
        if (!authController.signal.aborted) setAuthorizedState('error');
      });

    void getNonAuthorizedClosureApprovers({ signal: nonAuthController.signal })
      .then((r) => {
        if (nonAuthController.signal.aborted) return;
        if (!r.success) {
          setNonAuthorizedState('error');
          setFlashToast({ message: r.message || 'Failed to load special authorized closure approvers.', variant: 'error' });
          return;
        }
        const opts = r.data
          .map(nonAuthorizedClosureApproverLabel)
          .filter((x): x is { id: string; label: string } => Boolean(x));
        setNonAuthorizedOptions(
          ensureOption(
            opts,
            initialAssignment?.nonAuthorizedClosureApproverId,
            initialAssignment?.nonAuthorizedClosureApproverLabel,
          ),
        );
        setNonAuthorizedState('idle');
      })
      .catch(() => {
        if (!nonAuthController.signal.aborted) setNonAuthorizedState('error');
      });

    void listDocumentMovementOfficers(moverController.signal)
      .then((r) => {
        if (moverController.signal.aborted) return;
        if (!r.success) {
          setMoversState('error');
          setFlashToast({ message: r.message || 'Failed to load document movers.', variant: 'error' });
          return;
        }
        const opts = r.data.map(documentMoverLabel).filter((x): x is { id: string; label: string } => Boolean(x));
        setMoverOptions(
          ensureOption(opts, initialAssignment?.documentMoverId, initialAssignment?.documentMoverLabel),
        );
        setMoversState('idle');
      })
      .catch(() => {
        if (!moverController.signal.aborted) setMoversState('error');
      });

    return () => {
      authController.abort();
      nonAuthController.abort();
      moverController.abort();
    };
  }, [open, workflowInstanceStageUserTaskId, initialAssignment]);

  useEffect(() => {
    if (physicalRequired) return;
    /* eslint-disable react-hooks/set-state-in-effect */
    setDocumentMoverId('');
    setErrors((e) => ({ ...e, documentMoverId: '' }));
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [physicalRequired]);

  const authorizedDisabled = submitting || Boolean(nonAuthorizedId.trim()) || authorizedState === 'error';
  const nonAuthorizedDisabled = submitting || Boolean(authorizedId.trim()) || nonAuthorizedState === 'error';

  const formBlocked = blockedBySubsidiaryTasks;

  const canSubmit = useMemo(() => !submitting && !formBlocked, [submitting, formBlocked]);

  const closureApproverMissing = Boolean(errors.closureApprover) && !authorizedId.trim() && !nonAuthorizedId.trim();

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!workflowInstanceStageUserTaskId.trim()) next.workflowInstanceStageUserTaskId = 'User task id is required.';
    if (!authorizedId.trim() && !nonAuthorizedId.trim()) {
      next.closureApprover = 'Select a Pre-Authorized or Special Authorized closure approver.';
    }
    if (physicalRequired && !documentMoverId.trim()) next.documentMoverId = 'Select Document Mover is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const beginCancel = () => {
    if (submitting) return;
    setCancelConfirmOpen(true);
  };

  const confirmCancel = () => {
    setCancelConfirmOpen(false);
    onClose();
  };

  const submit = async () => {
    if (submitting) return;
    if (blockedBySubsidiaryTasks) {
      setFlashToast({
        message: blockingInfoMessage || 'There are pending sub tasks in this workflow stage.',
        variant: 'info',
      });
      return;
    }
    if (!validate()) return;
    setSubmitting(true);

    const res = await assignClosureApprover({
      workflowInstanceStageUserTaskId,
      authorizedClosureApproverId: authorizedId.trim() ? authorizedId : undefined,
      nonAuthorizedClosureApproverId: nonAuthorizedId.trim() ? nonAuthorizedId : undefined,
      physicalFileMovementRequired: physicalRequired,
      documentMoverId: physicalRequired ? documentMoverId : undefined,
    });

    setSubmitting(false);

    if (!res.success) {
      const msg = res.message || 'Assign closure approver failed.';
      setFlashToast({ message: msg, variant: 'error' });
      return;
    }

    setFlashToast({
      message: isUpdateMode
        ? 'Closure approver updated successfully.'
        : 'Closure approver assigned successfully.',
      variant: 'success',
    });
    onSuccess();
  };

  const authorizedPlaceholder = authorizedState === 'error' ? 'Failed to load' : 'Select Pre-Authorized Closure Approver';
  const nonAuthorizedPlaceholder =
    nonAuthorizedState === 'error' ? 'Failed to load' : 'Select Special Authorized Closure Approver';
  const moverPlaceholder = moversState === 'error' ? 'Failed to load' : 'Select Document Mover';

  return (
    <Modal
      open={open}
      title="Select User for Task Closure"
      onClose={() => {
        beginCancel();
      }}
    >
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Discard changes?"
        message="Your changes will be lost if you cancel."
        confirmDisabled={submitting}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={confirmCancel}
      />

      {errors.workflowInstanceStageUserTaskId ? (
        <div
          style={{
            marginBottom: 14,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(220, 53, 69, 0.08)',
            color: 'var(--t1)',
            fontSize: 12,
            border: '1px solid rgba(220, 53, 69, 0.18)',
          }}
          role="alert"
        >
          {errors.workflowInstanceStageUserTaskId}
        </div>
      ) : null}

      {blockedBySubsidiaryTasks ? (
        <div
          style={{
            marginBottom: 14,
            padding: '10px 12px',
            borderRadius: 10,
            background: 'rgba(220, 53, 69, 0.08)',
            color: 'var(--t1)',
            fontSize: 12,
            border: '1px solid rgba(220, 53, 69, 0.18)',
          }}
          role="status"
        >
          {blockingInfoMessage || 'There are pending sub tasks in this workflow stage. Please complete them to continue.'}
        </div>
      ) : null}

      <div className="cfg-assignment-form" style={{ padding: 0 }}>
        <div className="ccr-fg">
          <label>Pre-Authorized Closure Approver</label>
          <WizardSelect
            id="closure-authorized"
            hasError={closureApproverMissing}
            value={authorizedId}
            placeholder={authorizedPlaceholder}
            options={authorizedOptions}
            disabled={authorizedDisabled || formBlocked || cancelConfirmOpen}
            loading={authorizedState === 'loading'}
            emptyMessage={t('common.noUsersFound')}
            searchable
            onChange={(id) => {
              setAuthorizedId(id);
              setNonAuthorizedId('');
              setErrors((e) => ({ ...e, closureApprover: '' }));
            }}
          />
        </div>

        <div className="ccr-fg" style={{ marginTop: 14 }}>
          <label>Special Authorized Closure Approver</label>
          <WizardSelect
            id="closure-non-authorized"
            hasError={closureApproverMissing}
            value={nonAuthorizedId}
            placeholder={nonAuthorizedPlaceholder}
            options={nonAuthorizedOptions}
            disabled={nonAuthorizedDisabled || formBlocked || cancelConfirmOpen}
            loading={nonAuthorizedState === 'loading'}
            emptyMessage={t('common.noUsersFound')}
            searchable
            onChange={(id) => {
              setNonAuthorizedId(id);
              setAuthorizedId('');
              setErrors((e) => ({ ...e, closureApprover: '' }));
            }}
          />
          <FieldError message={errors.closureApprover} />
        </div>

        <div className="ccr-fg" style={{ marginTop: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={physicalRequired}
              disabled={submitting || formBlocked}
              onChange={(e) => setPhysicalRequired(e.target.checked)}
            />
            Physical File Movement Required
          </label>
        </div>

        {physicalRequired ? (
          <div className="ccr-fg" style={{ marginTop: 12 }}>
            <label>
              Select Document Mover <span className="req">*</span>
            </label>
            <WizardSelect
              id="closure-mover"
              hasError={Boolean(errors.documentMoverId)}
              value={documentMoverId}
              placeholder={moverPlaceholder}
              options={moverOptions}
              disabled={moversState === 'error' || submitting || formBlocked || cancelConfirmOpen}
              loading={moversState === 'loading'}
              emptyMessage={t('common.noUsersFound')}
              onChange={(id) => {
                setDocumentMoverId(id);
                setErrors((e) => ({ ...e, documentMoverId: '' }));
              }}
            />
            <FieldError message={errors.documentMoverId} />
          </div>
        ) : null}
      </div>

      <div className="cn-footer cn-footer--plain">
        <button className="mfb" type="button" disabled={submitting} onClick={beginCancel}>
          Cancel
        </button>
        <button className="mfb mfb-dk" type="button" disabled={!canSubmit} onClick={() => void submit()}>
          {submitting ? (isUpdateMode ? 'Updating…' : 'Selecting…') : isUpdateMode ? 'Update' : 'Select'}
        </button>
      </div>
    </Modal>
  );
}
